// molarity.js
import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Kalkulator Molaritas - Hitung Konsentrasi Molar dan Larutan Kimia";
const DESCRIPTION =
  "Kalkulator molaritas gratis - hitung konsentrasi molar, berat zat, dan volume secara akurat. Alat mudah untuk kimiawan dan pelajar untuk menghitung konsentrasi larutan kimia dengan penjelasan rumus dan contoh.";
const KEYWORDS =
  "kalkulator molaritas, hitung konsentrasi molar, rumus molaritas, hitung berat zat kimia, molarity calculator, hitung mol, konsentrasi larutan";
const OG_URL = "https://do-calculate.com/calculator/id/molarity/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Kalkulator Molaritas",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "ScientificApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Beranda",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Kalkulator",
        item: "https://do-calculate.com/id/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Kalkulator Molaritas",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Apa rumus molaritas?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Molaritas (M) sama dengan jumlah mol zat terlarut dibagi dengan volume larutan dalam liter. Rumus matematikanya adalah: M = n / V.",
        },
      },
      {
        "@type": "Question",
        name: "Bagaimana cara menghitung berat zat yang diperlukan untuk membuat larutan?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Berat (g) = Molaritas (mol/L) × Volume (L) × Berat Molekul (g/mol).",
        },
      },
    ],
  },
];

function MolarityCalculator() {
  const [molarity, setMolarity] = useState("");
  const [molecularWeight, setMolecularWeight] = useState("");
  const [volume, setVolume] = useState("");
  const [mass, setMass] = useState("");
  const [volumeUnit, setVolumeUnit] = useState("L");
  const [massUnit, setMassUnit] = useState("g");
  const [molarityUnit, setMolarityUnit] = useState("M");

  const [calculationMode, setCalculationMode] = useState("mass");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculate = () => {
    setError("");
    const M = parseFloat(molarity);
    const MW = parseFloat(molecularWeight);
    const V = parseFloat(volume);
    const m = parseFloat(mass);

    const getVolInL = (val) => {
      if (volumeUnit === "mL") return val / 1000;
      if (volumeUnit === "uL") return val / 1000000;
      return val;
    };

    const getMassInG = (val) => {
      if (massUnit === "mg") return val / 1000;
      if (massUnit === "ug") return val / 1000000;
      if (massUnit === "kg") return val * 1000;
      return val;
    };

    const getMolInM = (val) => {
      if (molarityUnit === "mM") return val / 1000;
      if (molarityUnit === "uM") return val / 1000000;
      if (molarityUnit === "nM") return val / 1000000000;
      return val;
    };

    try {
      if (calculationMode === "mass") {
        if (!molarity || !molecularWeight || !volume) return;
        const resMassG = getMolInM(M) * MW * getVolInL(V);
        setResult({
          value: resMassG,
          unit: "g",
          details: `Massa = ${M} ${molarityUnit} × ${MW} g/mol × ${V} ${volumeUnit}`,
        });
      } else if (calculationMode === "molarity") {
        if (!mass || !molecularWeight || !volume) return;
        const resM = getMassInG(m) / (MW * getVolInL(V));
        setResult({
          value: resM,
          unit: "M (mol/L)",
          details: `Molaritas = ${m} ${massUnit} / (${MW} g/mol × ${V} ${volumeUnit})`,
        });
      } else if (calculationMode === "volume") {
        if (!mass || !molarity || !molecularWeight) return;
        const resV = getMassInG(m) / (getMolInM(M) * MW);
        setResult({
          value: resV,
          unit: "Liter",
          details: `Volume = ${m} ${massUnit} / (${M} ${molarityUnit} × ${MW} g/mol)`,
        });
      }
    } catch (err) {
      setError("Harap pastikan memasukkan nilai yang valid.");
    }
  };

  useEffect(() => {
    calculate();
  }, [molarity, molecularWeight, volume, mass, volumeUnit, massUnit, molarityUnit, calculationMode]);

  const handleClear = () => {
    setMolarity("");
    setMolecularWeight("");
    setVolume("");
    setMass("");
    setResult(null);
    setError("");
  };

  return (
    <div className="ltr" >
      <SharedHelmet TITLE={TITLE} DESCRIPTION={DESCRIPTION} KEYWORDS={KEYWORDS} OG_URL={OG_URL} SCHEMA={SCHEMA} />

      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ padding: "15px", borderRadius: "5px" }}>
            <h1 style={{ fontSize: "22px", margin: 0 }}>Kalkulator Molaritas</h1>
          </div>

          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Kalkulator Molaritas</strong> membantu Anda menghitung konsentrasi larutan kimia dengan cepat dan akurat. Anda dapat menghitung massa yang diperlukan untuk membuat larutan, mengetahui molaritas yang dihasilkan, atau menentukan volume yang dibutuhkan. Alat ideal untuk laboratorium sains dan pelajar.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Masukkan Data untuk Perhitungan</h2>
              </div>

              <div className="row mb-3">
                <div className="col-12">
                  <label >Apa yang ingin Anda hitung?</label>
                  <select className="form-select" value={calculationMode} onChange={(e) => { setCalculationMode(e.target.value); handleClear(); }}>
                    <option value="mass">Massa yang Dibutuhkan (g)</option>
                    <option value="molarity">Molaritas (M)</option>
                    <option value="volume">Volume yang Dibutuhkan (L)</option>
                  </select>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label >Berat Molekul (g/mol)</label>
                  <input type="number" className="form-control" value={molecularWeight} onChange={(e) => setMolecularWeight(e.target.value)} placeholder="Contoh: 58,44" />
                </div>

                {calculationMode !== "molarity" && (
                  <div className="col-md-6 mb-3">
                    <label >Target Molaritas</label>
                    <div className="input-group">
                      <input type="number" className="form-control" value={molarity} onChange={(e) => setMolarity(e.target.value)} />
                      <select className="form-select" style={{ maxWidth: "80px" }} value={molarityUnit} onChange={(e) => setMolarityUnit(e.target.value)}>
                        <option value="M">M</option>
                        <option value="mM">mM</option>
                        <option value="uM">μM</option>
                      </select>
                    </div>
                  </div>
                )}

                {calculationMode !== "volume" && (
                  <div className="col-md-6 mb-3">
                    <label >Volume</label>
                    <div className="input-group">
                      <input type="number" className="form-control" value={volume} onChange={(e) => setVolume(e.target.value)} />
                      <select className="form-select" style={{ maxWidth: "80px" }} value={volumeUnit} onChange={(e) => setVolumeUnit(e.target.value)}>
                        <option value="L">L</option>
                        <option value="mL">mL</option>
                      </select>
                    </div>
                  </div>
                )}

                {calculationMode !== "mass" && (
                  <div className="col-md-6 mb-3">
                    <label >Massa / Berat</label>
                    <div className="input-group">
                      <input type="number" className="form-control" value={mass} onChange={(e) => setMass(e.target.value)} />
                      <select className="form-select" style={{ maxWidth: "80px" }} value={massUnit} onChange={(e) => setMassUnit(e.target.value)}>
                        <option value="g">g</option>
                        <option value="mg">mg</option>
                        <option value="kg">kg</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div className="text-center mt-3">
                <button onClick={handleClear} className="btn btn-secondary px-5"style={{marginRight:'3px'}}>Hapus</button>
              </div>

              {error && <div className="alert alert-danger mt-3">{error}</div>}

              {result && (
                <div className="mt-4 result-box">
                  <table className="table bmi-table">
                    <thead>
                      <tr>
                        <th className="bmi-header text-center">Hasil</th>
                        <th className="bmi-header text-center">Nilai</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="text-center">Nilai yang Dihitung</td>
                        <td className="text-center" style={{ fontSize: "20px", fontWeight: "bold", color: "#1a5f3f" }}>
                          {result.value.toFixed(4)} {result.unit}
                         </td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="alert alert-info text-center" style={{ fontSize: "14px" }}>
                    <strong>Metode Perhitungan:</strong> {result.details}
                  </div>
                </div>
              )}
            </div>

            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><strong>Apa itu Kalkulator Molaritas?</strong></div>
              <div className="info-section-body">
                <p>Kalkulator molaritas adalah alat ilmiah yang digunakan untuk menentukan konsentrasi larutan kimia, yang dinyatakan dalam molaritas (M). Molaritas adalah jumlah mol zat terlarut dalam satu liter larutan.</p>
                <p>Kalkulator ini terutama digunakan untuk:</p>
                <ul>
                  <li>Menentukan jumlah zat padat (dalam gram) yang diperlukan untuk membuat larutan dengan konsentrasi tertentu.</li>
                  <li>Menghitung konsentrasi akhir larutan ketika sejumlah zat yang diketahui dilarutkan dalam volume tertentu.</li>
                  <li>Mengonversi satuan antara mililiter dan liter, serta antara miligram, gram, dan nanomolar.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Penggunaan Kalkulator Molaritas</strong></div>
              <div className="info-section-body">
                <ul>
                  <li><strong>Penelitian Ilmiah:</strong> Menyiapkan reagen dan larutan penyangga (Buffer) dengan presisi tinggi.</li>
                  <li><strong>Pendidikan Akademik:</strong> Membantu siswa kimia memecahkan masalah yang berkaitan dengan konsentrasi dan berat molekul.</li>
                  <li><strong>Industri Farmasi:</strong> Menghitung proporsi bahan aktif dalam obat-obatan cair.</li>
                  <li><strong>Analisis Medis:</strong> Menyesuaikan konsentrasi larutan yang digunakan dalam peralatan analisis laboratorium.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Rumus Kimia yang Digunakan</strong></div>
              <div className="info-section-body">
                <p>Kalkulator didasarkan pada rumus dasar berikut:</p>
                <table className="table bmi-table">
                  <thead>
                    <tr>
                      <th>Yang Dicari</th>
                      <th>Rumus</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Molaritas (M)</td>
                      <td>n (jumlah mol) / V (volume dalam liter)</td>
                    </tr>
                    <tr>
                      <td>Massa (g)</td>
                      <td>M (molaritas) × MW (berat molekul) × V (volume dalam liter)</td>
                    </tr>
                    <tr>
                      <td>Jumlah Mol (n)</td>
                      <td>m (massa dalam gram) / MW (berat molekul)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Contoh Praktis</strong></div>
              <div className="info-section-body">
                <p><strong>Contoh 1:</strong> Menyiapkan larutan garam dapur (NaCl) dengan konsentrasi 0,5 M dalam volume 1 liter. (Berat molekul NaCl adalah 58,44 g/mol).</p>
                <p>Massa = 0,5 × 58,44 × 1 = 29,22 gram.</p>
                <p><strong>Contoh 2:</strong> Melarutkan 10 gram glukosa (berat molekul 180,16) dalam 500 mL air.</p>
                <p>Molaritas = 10 / (180,16 × 0,5) = 0,111 M.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Tips Penggunaan yang Benar</strong></div>
              <div className="info-section-body">
                <ul>
                  <li>Pastikan satuan volume (liter atau mililiter) konsisten sebelum memulai perhitungan manual.</li>
                  <li>Gunakan berat molekul yang akurat untuk zat kimia (dapat ditemukan pada label kemasan).</li>
                  <li>Saat menyiapkan larutan, tambahkan zat terlarut terlebih dahulu lalu tambahkan air hingga mencapai tanda yang diperlukan untuk memastikan akurasi volume total.</li>
                  <li>Hindari mencampuradukkan molaritas (M) dengan molalitas (m), karena yang terakhir bergantung pada massa pelarut, bukan volume larutan.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Pertanyaan Umum (FAQ)</strong></div>
              <div className="info-section-body">
                <div className="mb-3">
                  <strong>Apa satuan molaritas?</strong>
                  <p>Satuan molaritas adalah "mol per liter" (mol/L), disingkat dengan simbol M. Misalnya, larutan 1M berarti ada satu mol zat dalam satu liter larutan.</p>
                </div>
                <div className="mb-3">
                  <strong>Apakah molaritas dipengaruhi oleh suhu?</strong>
                  <p>Ya, karena volume berubah seiring perubahan suhu (pemuaian cairan), molaritas dapat sedikit berubah dengan meningkatnya suhu, tidak seperti massa yang tetap konstan.</p>
                </div>
                <div className="mb-3">
                  <strong>Bagaimana cara mengonversi dari miligram ke gram?</strong>
                  <p>Satu gram sama dengan 1000 miligram. Untuk mengonversi dari miligram ke gram, bagi nilainya dengan 1000 (misalnya: 500 mg menjadi 0,5 gram).</p>
                </div>
                <div className="mb-3">
                  <strong>Mengapa saya memerlukan berat molekul?</strong>
                  <p>Berat molekul adalah penghubung antara massa fisik (gram) dan jumlah kimia (mol). Tanpanya, kita tidak dapat mengetahui jumlah molekul dalam berat tertentu.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MolarityCalculator;